import { inject, Injectable } from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { signalMethod } from '@ngrx/signals';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { DivisionConfig, DivisionConfigData, RequiredDivisionConfigData } from '@r1-compass-library/models';
import {
    DivisionCriteriaSourceSettings,
    DivisionCriteriaSourceSettingsForm,
} from '@r1-compass-library/models/division/division-criteria-source-settings.model';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';

@Injectable()
export class DivisionCriteriaSourceSettingsFormService {
    readonly #nonNullableFormBuilder = inject(NonNullableFormBuilder);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #globalDivisionStore = inject(GlobalDivisionsStore);

    public criteriaSourceSettingsForm: FormGroup<DivisionCriteriaSourceSettingsForm> =
        this.#nonNullableFormBuilder.group<DivisionCriteriaSourceSettingsForm>({
            salesHistoryTable: this.#nonNullableFormBuilder.control(undefined),
            salesHistoryCmtColumn: this.#nonNullableFormBuilder.control(undefined),
            salesHistorySelfMfgColumn: this.#nonNullableFormBuilder.control(undefined),
            salesHistorySspColumn: this.#nonNullableFormBuilder.control(undefined),
            salesHistoryChannelsColumn: this.#nonNullableFormBuilder.control(undefined),
            salesHistoryMetricsColumn: this.#nonNullableFormBuilder.control(undefined),
            salesHistoryProductLabelColumn: this.#nonNullableFormBuilder.control(undefined),
            salesHistoryVendorsTable: this.#nonNullableFormBuilder.control(undefined),
            salesHistoryVendorsColumn: this.#nonNullableFormBuilder.control(undefined),
            webActivityTable: this.#nonNullableFormBuilder.control(undefined),
            webActivityCmtColumn: this.#nonNullableFormBuilder.control(undefined),
        });

    constructor() {
        this.setDivisionCriteriaSourceSettingsFormData(this.#globalLoggedInUserStore.selectedDivisionConfig);
    }

    private setDivisionCriteriaSourceSettingsFormData = signalMethod<DivisionConfig | undefined>(division => {
        if (division) {
            const criteriaSourceSettings: DivisionCriteriaSourceSettings | undefined =
                division.config?.criteriaSourceSettings;
            if (criteriaSourceSettings) {
                this.criteriaSourceSettingsForm.patchValue({
                    salesHistoryTable: criteriaSourceSettings.salesHistoryTable ?? undefined,
                    salesHistoryCmtColumn: criteriaSourceSettings.salesHistoryCmtColumn ?? undefined,
                    salesHistorySelfMfgColumn: criteriaSourceSettings.salesHistorySelfMfgColumn ?? undefined,
                    salesHistorySspColumn: criteriaSourceSettings.salesHistorySspColumn ?? undefined,
                    salesHistoryChannelsColumn: criteriaSourceSettings.salesHistoryChannelsColumn ?? undefined,
                    salesHistoryMetricsColumn: criteriaSourceSettings.salesHistoryMetricsColumn ?? undefined,
                    salesHistoryProductLabelColumn: criteriaSourceSettings.salesHistoryProductLabelColumn ?? undefined,
                    salesHistoryVendorsTable: criteriaSourceSettings.salesHistoryVendorsTable ?? undefined,
                    salesHistoryVendorsColumn: criteriaSourceSettings.salesHistoryVendorsColumn ?? undefined,
                    webActivityTable: criteriaSourceSettings.webActivityTable ?? undefined,
                    webActivityCmtColumn: criteriaSourceSettings.webActivityCmtColumn ?? undefined,
                });
            }
        }
    });

    public saveDivisionCriteriaSourceSettings() {
        const criteriaSourceSettings: DivisionCriteriaSourceSettings = this.criteriaSourceSettingsForm.getRawValue();
        const divisionId = this.#globalLoggedInUserStore.selectedDivisionConfig()?.id;
        const loggedInUserId = this.#globalLoggedInUserStore.selectedUserId();

        if (divisionId && loggedInUserId) {
            const config: RequiredDivisionConfigData = {
                id: divisionId,
                updatedBy: loggedInUserId,
            };
            const data: Partial<DivisionConfigData> = {
                criteriaSourceSettings: criteriaSourceSettings,
            };

            return this.#globalDivisionStore.saveDivisionConfigAndData({ config, data });
        } else {
            return noopFormSaveMutationResult();
        }
    }
}
